/**
 * W65 — CreditSplitsPanel
 * Shows opportunity_credits rows: avatar, name, role, credit %.
 * Sum row: green if =100, red if !=100.
 * "Add split" button opens user picker (minimal inline for now).
 *
 * W67 override rule: manager_override credit kind is hidden from the
 * kind selector for all roles. Only AE role may assign credit kinds;
 * manager_override as a new write is deprecated per CANON §7.
 * Historical manager_override rows still display correctly.
 */
import { useState } from "react";
import { getSession, type SessionUser } from "@/lib/session";

const CREDIT_KIND_LABELS: Record<string, string> = {
  primary_ae:       "Primary AE",
  joint_ae:         "Joint AE",
  bdr:              "BDR",
  sales_engineer:   "Sales Engineer",
  manager_override: "Manager Override",
};

const CREDIT_KIND_COLORS: Record<string, string> = {
  primary_ae:       "#0B0B0F",
  joint_ae:         "#0B0B0F",
  bdr:              "#EA580C",
  sales_engineer:   "#16A34A",
  manager_override: "#DC2626", // canon red per CANON.md §5 (off-canon deep red retired)
};

interface Credit {
  id: string;
  user_id: string;
  user_name?: string | null;
  credit_kind: string;
  credit_pct: number;
}

interface CreditSplitsPanelProps {
  opportunityId: string;
  credits: Credit[];
  onSaved: () => void;
}

function initials(name: string | null | undefined): string {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

export function CreditSplitsPanel({ opportunityId, credits, onSaved }: CreditSplitsPanelProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [newUserId, setNewUserId] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newKind, setNewKind] = useState("primary_ae");
  const [newPct, setNewPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const session = getSession();
  const token = session ? (session as any).access_token ?? (session as any).token ?? "" : "";
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  // W67: AE-only override rule — hide manager_override credit kind from selector
  const userRole = (session as SessionUser | null)?.role ?? null;
  const selectableKinds = Object.entries(CREDIT_KIND_LABELS).filter(
    ([k]) => k !== "manager_override"
  );

  const totalPct = credits.reduce((s, c) => s + Number(c.credit_pct), 0);
  const sumColor = Math.abs(totalPct - 100) < 0.001 ? "#16A34A" : "#DC2626";

  async function handleAddCredit() {
    if (!newUserId.trim()) { setError("User ID required"); return; }
    if (newPct <= 0 || newPct > 100) { setError("Credit % must be 1–100"); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/opportunities/${opportunityId}/credits`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ user_id: newUserId.trim(), credit_kind: newKind, credit_pct: newPct }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? "Failed to save credit");
      }
      setShowAdd(false);
      setNewUserId("");
      setNewUserName("");
      setNewKind("primary_ae");
      setNewPct(0);
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 8,
        border: "1px solid #E5E5E7",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 16px 8px",
          borderBottom: "1px solid #E5E5E7",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>Credit Splits</h3>
        {!showAdd && (
          <button
            onClick={() => { setShowAdd(true); setError(null); }}
            style={{ fontSize: 12, fontWeight: 600, color: "#0B0B0F", background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}
          >
            + Add split
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 12, padding: "8px 16px" }}>{error}</div>
      )}

      <div style={{ padding: "8px 0" }}>
        {credits.length === 0 && (
          <div style={{ fontSize: 12, color: "#8E8E94", padding: "8px 16px" }}>No credit splits configured.</div>
        )}
        {credits.map((c) => {
          const kindColor = CREDIT_KIND_COLORS[c.credit_kind] ?? "#6B7280";
          return (
            <div
              key={c.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "7px 16px",
                borderBottom: "1px solid #EFEFEE",
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  background: kindColor,
                  color: "#FFFFFF",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {initials(c.user_name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#2A2A2E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.user_name ?? c.user_id}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: kindColor,
                    textTransform: "uppercase",
                  }}
                >
                  {CREDIT_KIND_LABELS[c.credit_kind] ?? c.credit_kind}
                </div>
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "#0B0B0F",
                  minWidth: 42,
                  textAlign: "right",
                }}
              >
                {Number(c.credit_pct).toFixed(0)}%
              </div>
            </div>
          );
        })}
      </div>

      {/* Sum row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 16px",
          borderTop: "2px solid #E5E5E7",
          background: "#FAFAF7",
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: "#2A2A2E" }}>Total</span>
        <span
          style={{
            fontSize: 16,
            fontWeight: 800,
            color: sumColor,
          }}
        >
          {totalPct.toFixed(0)}%
          {Math.abs(totalPct - 100) > 0.001 && (
            <span style={{ fontSize: 10, marginLeft: 6, fontWeight: 600 }}>
              {totalPct < 100 ? `(${(100 - totalPct).toFixed(0)}% unallocated)` : `(${(totalPct - 100).toFixed(0)}% over)` }
            </span>
          )}
        </span>
      </div>

      {/* BDR note */}
      {credits.some((c) => c.credit_kind === "bdr") && (
        <div style={{ padding: "6px 16px 10px", fontSize: 11, color: "#8E8E94" }}>
          BDR credit slice: typically 10–20% per industry norm.
        </div>
      )}

      {/* Add split form */}
      {showAdd && (
        <div
          style={{
            borderTop: "1px solid #E5E5E7",
            padding: "12px 16px",
            background: "#FAFAF7",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0B0B0F", marginBottom: 2 }}>Add Credit Split</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <input
              placeholder="User ID (UUID)"
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
              style={{ border: "1px solid #C9C9CC", borderRadius: 4, padding: "5px 8px", fontSize: 12 }}
            />
            <input
              placeholder="Display name (optional)"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              style={{ border: "1px solid #C9C9CC", borderRadius: 4, padding: "5px 8px", fontSize: 12 }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={newKind}
                onChange={(e) => setNewKind(e.target.value)}
                style={{ flex: 1, border: "1px solid #C9C9CC", borderRadius: 4, padding: "5px 8px", fontSize: 12 }}
              >
                {selectableKinds.map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={newPct}
                  onChange={(e) => setNewPct(Number(e.target.value))}
                  style={{ width: 56, border: "1px solid #C9C9CC", borderRadius: 4, padding: "5px 6px", fontSize: 12, textAlign: "right" }}
                />
                <span style={{ fontSize: 12, color: "#6B7280" }}>%</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleAddCredit}
              disabled={saving}
              style={{ background: "#0B0B0F", color: "#FFFFFF", border: "none", borderRadius: 4, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer" }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => { setShowAdd(false); setError(null); }}
              style={{ background: "none", color: "#6B7280", border: "1px solid #C9C9CC", borderRadius: 4, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
